import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, HelpCircle, ChevronRight, ChevronDown } from 'lucide-react';
import DrSkuGlobalSettings from '@/components/admin/DrSkuGlobalSettings';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface EditableSkuTableProps {
  data: any[];
  columns: string[];
  organisationId: string;
  connectionId?: string;
  isAdmin?: boolean;
  organisation?: any;
  onDataRefresh?: () => void; // Add callback to refresh data from parent
  showGlobalSettings?: boolean;
  onGlobalSettingsToggle?: () => void;
  isLoading?: boolean;
  areTotalsLoading?: boolean;
  totalsData?: any; // API totals from progressive reports
  totalsUnavailable?: boolean;
}

const EditableSkuTable: React.FC<EditableSkuTableProps> = ({
  data,
  columns,
  organisationId,
  connectionId = '',
  isAdmin = false,
  organisation,
  onDataRefresh,
  showGlobalSettings = false,
  onGlobalSettingsToggle,
  isLoading = false,
  areTotalsLoading = false,
  totalsData,
  totalsUnavailable = false
}) => {

  const { toast } = useToast();
  const [currentDrSkuPercentage, setCurrentDrSkuPercentage] = useState<number>(Number(organisation?.dr_sku_percentage ?? 0));
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  };

  const getConstituents = (row: any): Array<{ asin?: string; seller_sku?: string; barcode?: string }> => {
    let cs = row?.constituent_skus;
    if (typeof cs === 'string') {
      try { cs = JSON.parse(cs); } catch { return []; }
    }
    if (!Array.isArray(cs)) return [];
    return cs.filter((c: any) => c && typeof c === 'object' && (c.asin || c.seller_sku || c.barcode));
  };

  const getDisplayConstituents = (row: any): Array<{ asin?: string; seller_sku?: string; barcode?: string }> => {
    const parsed = getConstituents(row);
    if (parsed.length > 0) return parsed;
    // Fallback for identifier_missing rows with no constituent_skus
    if (row.identifier_missing === true) {
      const fallback = row.SKU || row.Product;
      if (fallback) return [{ seller_sku: fallback }];
    }
    return [];
  };

  const hasConstituents = (row: any): boolean => {
    return getDisplayConstituents(row).length >= 1;
  };

  const handleGlobalSettingsUpdate = (newValue: number) => {
    onGlobalSettingsToggle?.();
    setCurrentDrSkuPercentage(newValue);

    // Trigger data refresh from parent to get updated values
    onDataRefresh?.();

    toast({
      title: "Global settings updated",
      description: `DR SKU percentage updated to ${newValue}% for all SKUs`,
    });
  };

  const columnTooltips: Record<string, { title: string; formula: string; aggregation?: string }> = {
    '∑: GEV $': { title: 'Gross Expected Value', formula: 'For each Product: GEV = Stock Held × Avg GMV', aggregation: 'Total GEV = Sum of all per-Product GEVs' },
    '∑: NEV $': { title: 'Net Expected Value', formula: 'For each Product: NEV = Stock Held × Avg NMV', aggregation: 'Total NEV = Sum of all per-Product NEVs' },
    '∑: NERV $': { title: 'Net Expected Realizable Value', formula: 'For each Product: NERV = MIN(Stock, Sold) × Avg NMV × (1 − DR%)', aggregation: 'Total NERV = Sum of all per-Product NERVs' },
    '∑: GMV $': { title: 'Gross Merchandise Value', formula: 'Sum of gross sales (trailing 12 months)' },
    '∑: NMV $': { title: 'Net Merchandise Value', formula: 'Sum of net sales after discounts (trailing 12 months)' },
    '∑: GERV $': { title: 'Gross Expected Retail Value', formula: 'Sum for trailing 12 months' },
    'AVG GMV $': { title: 'Average Gross Merchandise Value', formula: '∑ GMV ÷ Units Sold (trailing 12 months)' },
    'AVG NMV $': { title: 'Average Net Merchandise Value', formula: '∑ NMV ÷ Units Sold (trailing 12 months)' },
    'NERV (in 1 month)': { title: 'NERV — 1 Month Projection', formula: 'Stock - MIN(Stock, Units Sold / Days in Period * 30) × Avg NMV × (1 − DR%)' },
    'NERV (in 3 months)': { title: 'NERV — 3 Month Projection', formula: 'Stock - MIN(Stock, Units Sold / Days in Period * 90) × Avg NMV × (1 − DR%)' },
    'NERV (in 6 months)': { title: 'NERV — 6 Month Projection', formula: 'Stock - MIN(Stock, Units Sold / Days in Period * 180) × Avg NMV × (1 − DR%)' },
  };

  const formatCellValue = (value: any, columnName: string): string => {

    // Handle Days Until Stockout formatting
    if (columnName === 'Days Until Stockout') {
      if (value === null || value === undefined || value === '') {
        return 'No sales';
      }
      const numValue = parseFloat(value.toString().replace(/,/g, ''))
      if (isNaN(numValue)) {
        return '0';
      }
      if (numValue === 0 || numValue < 0.05) {
        return '0';
      }
      return `${numValue.toFixed()}`;
    }

    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object') {
      try {
        if (value.shop_money && value.presentment_money) {
          return `${value.shop_money.amount || 0} ${value.shop_money.currency_code || ''}`;
        }

        if (Array.isArray(value)) {
          return value.map(item => typeof item === 'object' ? 'Item' : String(item)).join(', ');
        }

        const simpleText = JSON.stringify(value);
        return simpleText.length > 100 ? simpleText.substring(0, 97) + '...' : simpleText;
      } catch (error) {
        console.error('Error formatting object value:', error);
        return '[Complex Object]';
      }
    }

    // Special formatting for percentage columns
    if (columnName === 'DR: SKU %') {
      const numValue = parseFloat(value.toString());
      if (isNaN(numValue)) {
        return ''; // Show empty instead of NaN%
      }
      return `${numValue.toFixed(2)}%`;
    }

    // Handle currency formatting for NEV/NERV columns
    if (columnName === '∑: GEV $' || columnName === '∑: NEV $' || columnName === '∑: NERV $' ||
      columnName === 'NERV (in 1 month)' || columnName === 'NERV (in 3 months)' || columnName === 'NERV (in 6 months)') {
      const numValue = parseFloat(value.toString().replace(/,/g, ''));
      if (isNaN(numValue)) {
        return '$0.00';
      }
      return `$${numValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }

    return String(value);
  };

  const calculateSummaryRow = () => {
    // When totals failed (resource limit or other error), show nothing rather than wrong partial data
    if (totalsUnavailable) return null;

    // If totals are still loading for multi-connection mode, show loading state
    if (areTotalsLoading && !totalsData) {
      const summaryRow: any = {};
      columns.forEach(column => {
        if (column === 'SKU' || column === 'Product') {
          summaryRow[column] = 'TOTAL';
        } else {
          summaryRow[column] = ''; // Show empty during loading
        }
      });
      return summaryRow;
    }

    // Use API totals if available, otherwise calculate from visible rows
    if (totalsData) {
      const summaryRow: any = {};

      columns.forEach(column => {
        if (column === 'SKU' || column === 'Product') {
          summaryRow[column] = 'TOTAL';
        } else {
          // Map column names to API totals field names
          const fieldMapping: Record<string, string> = {
            '∑: GMV $': 'Total GMV $',
            '∑: NMV $': 'Total NMV $',
            'Fee Allocation $': 'Total Fee Allocation $',
            'AVG GMV $': 'AVG GMV $',
            'AVG NMV $': 'AVG NMV $',
            '∑: Units Sold': 'Total Units Sold',
            'Stock Held': 'Total Stock Held',
            'Days Until Stockout': 'Days Until Stockout',
            '∑: GEV $': 'Total GEV $',
            '∑: NEV $': 'Total NEV $',
            'DR: SKU %': 'DR: SKU %',
            '∑: NERV $': '∑: NERV $',
            'NERV (in 1 month)': 'NERV (in 1 month)',
            'NERV (in 3 months)': 'NERV (in 3 months)',
            'NERV (in 6 months)': 'NERV (in 6 months)'
          };

          const apiFieldName = fieldMapping[column];
          if (apiFieldName && totalsData[apiFieldName] !== undefined) {
            // Use the API totals value
            const value = totalsData[apiFieldName];

            // Handle different value types
            if (typeof value === 'string') {
              // Parse and reformat numeric strings with proper locale formatting
              const parsed = parseFloat(value.replace(/[,$]/g, ''));
              if (!isNaN(parsed)) {
                if (column.includes('$')) {
                  summaryRow[column] = parsed.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  });
                } else {
                  summaryRow[column] = parsed.toLocaleString('en-US');
                }
              } else {
                summaryRow[column] = value;
              }
            } else if (typeof value === 'number') {
              // Format numbers appropriately
              if (column.includes('$')) {
                summaryRow[column] = value.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
              } else if (column === 'Days Until Stockout') {
                // Round Days Until Stockout to whole number
                summaryRow[column] = Math.round(value).toLocaleString('en-US');
              } else {
                summaryRow[column] = value.toLocaleString('en-US');
              }
            } else {
              summaryRow[column] = String(value || '');
            }
          } else {
            summaryRow[column] = '';
          }
        }
      });

      return summaryRow;
    }

    // Fallback: calculate from visible rows (original behavior)
    if (!data || data.length === 0) return null;

    const numericalColumns = [
      '∑: GMV $', '∑: NMV $', '∑: Units Sold',
      'Stock Held', '∑: GEV $', '∑: NEV $', '∑: NERV $',
      'Fee Allocation $',
      'NERV (in 1 month)', 'NERV (in 3 months)', 'NERV (in 6 months)'
    ];

    const averageColumns = ['AVG GMV $', 'AVG NMV $'];

    const summaryRow: any = {};

    columns.forEach(column => {
      if (column === 'SKU' || column === 'Product') {
        summaryRow[column] = 'TOTAL';
      } else if (numericalColumns.includes(column)) {
        const sum = data.reduce((acc, row) => {
          const value = row[column];
          if (value === null || value === undefined) return acc;

          // Handle formatted currency strings (remove commas and convert to number)
          let numValue = 0;
          if (typeof value === 'string') {
            const cleanValue = value.replace(/[,$]/g, '');
            numValue = parseFloat(cleanValue) || 0;
          } else {
            numValue = parseFloat(value.toString()) || 0;
          }

          return acc + numValue;
        }, 0);

        // Format the sum based on the column type
        if (column.includes('$')) {
          summaryRow[column] = sum.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        } else {
          summaryRow[column] = sum.toLocaleString('en-US');
        }
      } else if (averageColumns.includes(column)) {
        // Calculate weighted average from totals
        const totalGMV = summaryRow['∑: GMV $'] ? parseFloat(summaryRow['∑: GMV $'].replace(/,/g, '')) : 0;
        const totalNMV = summaryRow['∑: NMV $'] ? parseFloat(summaryRow['∑: NMV $'].replace(/,/g, '')) : 0;
        const totalUnits = summaryRow['∑: Units Sold'] ? parseFloat(summaryRow['∑: Units Sold'].replace(/,/g, '')) : 0;

        if (column === 'AVG GMV $' && totalUnits > 0) {
          const avgGMV = totalGMV / totalUnits;
          summaryRow[column] = avgGMV.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        } else if (column === 'AVG NMV $' && totalUnits > 0) {
          const avgNMV = totalNMV / totalUnits;
          summaryRow[column] = avgNMV.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        } else {
          summaryRow[column] = '0.00';
        }
      } else if (column === 'Days Until Stockout') {
        // This should ideally come from API totals, but calculate as fallback
        // Note: This fallback may not be accurate for multi-connection scenarios
        summaryRow[column] = '';
      } else {
        summaryRow[column] = '';
      }
    });

    return summaryRow;
  };

  const isDrSkuPercentageColumn = (columnName: string) => columnName === 'DR: SKU %';

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-4">
        {showGlobalSettings && isAdmin && (
          <DrSkuGlobalSettings
            organisationId={organisationId}
            currentValue={currentDrSkuPercentage}
            onUpdate={handleGlobalSettingsUpdate}
          />
        )}

        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex items-center justify-center py-2 px-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-600" />
            <span className="text-sm text-blue-700 font-medium">
              Loading data... Summary totals will update as more data loads.
            </span>
          </div>
        )}

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <Table containerClassName="max-h-[600px] overflow-auto">
            <TableHeader>
              <TableRow>
                {columns.map((column) => {
                  const tooltip = columnTooltips[column];
                  const displayColumn = (column === 'SKU' || column === 'Product') ? 'Product' : column;
                  const headerText = displayColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                  return (
                    <TableHead key={column} className="sticky top-0 z-10 bg-white font-semibold border-b-2 border-gray-200 whitespace-nowrap">
                      {tooltip ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 cursor-help">
                              {headerText}
                              <HelpCircle className="h-3 w-3 text-muted-foreground" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-md">
                            <p className="font-semibold">{tooltip.title}</p>
                            <p className="text-xs text-muted-foreground">{tooltip.formula}</p>
                            {tooltip.aggregation && (
                              <p className="text-xs text-muted-foreground italic mt-1">{tooltip.aggregation}</p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        headerText
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length > 0 ? (
                <>
                  {/* Summary Row */}
                  {(() => {
                    const summaryRow = calculateSummaryRow();
                    return summaryRow ? (
                      <TableRow className="bg-blue-50 font-semibold border-b-2 border-blue-200">
                        {columns.map((column) => (
                          <TableCell key={`summary-${column}`} className="truncate max-w-xs font-bold">
                            {areTotalsLoading && column !== 'SKU' && column !== 'Product' ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-3 w-3 animate-spin" />
                              </div>
                            ) : (
                              formatCellValue(summaryRow[column], column)
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ) : null;
                  })()}

                  {/* Data Rows */}
                  {data.map((row, rowIndex) => (
                    <React.Fragment key={rowIndex}>
                      <TableRow
                        className={`group hover:bg-gray-50 ${hasConstituents(row) ? 'cursor-pointer' : ''}`}
                        onClick={hasConstituents(row) ? () => toggleRowExpansion(rowIndex) : undefined}
                      >
                        {columns.map((column, colIndex) => (
                          <TableCell key={`${rowIndex}-${column}`} className="truncate max-w-xs">
                            <span className="inline-flex items-center gap-1">
                              {colIndex === 0 && hasConstituents(row) && (
                                expandedRows.has(rowIndex)
                                  ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              )}
                              {colIndex === 0 && row.identifier_missing === true
                                ? ''
                                : formatCellValue(row[column], column)
                              }
                            </span>
                          </TableCell>
                        ))}
                      </TableRow>
                      {/* Expanded constituent row */}
                      {expandedRows.has(rowIndex) && hasConstituents(row) && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={columns.length} className="py-2 px-6">
                            <div className="text-xs text-muted-foreground space-y-1">
                              <p className="font-semibold mb-1">Grouped Identifiers:</p>
                              {getDisplayConstituents(row).map((c, i) => {
                                const cleanAsin = c.asin?.trim() && !['', '-', '—'].includes(c.asin.trim()) ? c.asin.trim() : null;
                                const cleanBarcode = c.barcode?.trim() && !['', '-', '—'].includes(c.barcode.trim()) ? c.barcode.trim() : null;
                                return (
                                  <div key={i} className="flex items-center gap-3 pl-4">
                                    {cleanAsin ? (
                                      <>
                                        <span>ASIN: <span className="font-mono font-medium text-foreground">{cleanAsin}</span></span>
                                        <span className="text-muted-foreground">|</span>
                                      </>
                                    ) : cleanBarcode ? (
                                      <>
                                        <span>Barcode: <span className="font-mono font-medium text-foreground">{cleanBarcode}</span></span>
                                        <span className="text-muted-foreground">|</span>
                                      </>
                                    ) : null}
                                    <span>Seller SKU: <span className="font-mono font-medium text-foreground">{c.seller_sku || '—'}</span></span>
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-8 text-gray-500">
                    Data is syncing...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default EditableSkuTable;